import type { HubSpotProperty } from '@/store/features/crm.feature';
import type {
  CrmTriggerConfig,
  SalesforceTriggerCondition,
  SalesforceTriggerFieldType,
  SalesforceTriggerGroup,
} from '@/types/crm-enrichment-playbook';
import { isRecord } from '@/lib/is-record';

export type SalesforceFieldType = SalesforceTriggerFieldType;

export interface TriggerOperator {
  value: string;
  label: string;
  disabled?: boolean;
}

export const VALUELESS_TRIGGER_OPERATORS = new Set(['exists', 'not_exists']);

/**
 * One sentence-case label per operator, shared by trigger conditions and
 * Branch so the same operator reads the same in both. A date reads its
 * comparisons as days (`operatorLabel`).
 */
export const OPERATOR_LABELS: Readonly<Record<string, string>> = {
  equals: 'Is',
  not_equals: 'Is not',
  contains: 'Contains',
  not_contains: 'Does not contain',
  starts_with: 'Starts with',
  ends_with: 'Ends with',
  in: 'Is any of',
  not_in: 'Is none of',
  includes: 'Includes any of',
  excludes: 'Excludes all of',
  greater_than: 'Is greater than',
  less_than: 'Is less than',
  greater_than_or_equal: 'Is at least',
  less_than_or_equal: 'Is at most',
  exists: 'Is known',
  not_exists: 'Is unknown',
  before: 'Is before',
  after: 'Is after',
  within_last_days: 'In the last',
  more_than_days_ago: 'More than',
  within_next_days: 'In the next',
  in_list: 'Is in list',
  not_in_list: 'Is not in list',
};

const DATE_OPERATOR_LABELS: Readonly<Record<string, string>> = {
  equals: 'Is on',
  not_equals: 'Is not on',
  greater_than: 'Is after',
  less_than: 'Is before',
  greater_than_or_equal: 'Is on or after',
  less_than_or_equal: 'Is on or before',
};

export function operatorLabel(operator: string, fieldType?: string): string {
  return (
    (fieldType === 'date' ? DATE_OPERATOR_LABELS[operator] : undefined) ??
    OPERATOR_LABELS[operator] ??
    operator
  );
}

const operators = (
  fieldType: SalesforceFieldType,
  values: string[]
): TriggerOperator[] =>
  values.map((value) => ({ value, label: operatorLabel(value, fieldType) }));

/**
 * Operators accepted by the Salesforce trigger compiler (playbook_poll.py)
 * and HubSpot search. Keep this map in lockstep with the backend validator.
 * Multipicklists deliberately use INCLUDES/EXCLUDES semantics instead of
 * normal SOQL IN/NOT IN. Text and number take a list (a set of record IDs).
 */
export const SALESFORCE_OPERATORS_BY_TYPE: Record<
  SalesforceFieldType,
  TriggerOperator[]
> = {
  text: operators('text', [
    'equals',
    'not_equals',
    'contains',
    'not_contains',
    'in',
    'not_in',
    'exists',
    'not_exists',
  ]),
  picklist: operators('picklist', [
    'equals',
    'not_equals',
    'in',
    'not_in',
    'exists',
    'not_exists',
  ]),
  multipicklist: operators('multipicklist', [
    'includes',
    'excludes',
    'exists',
    'not_exists',
  ]),
  boolean: operators('boolean', [
    'equals',
    'not_equals',
    'exists',
    'not_exists',
  ]),
  number: operators('number', [
    'equals',
    'not_equals',
    'greater_than',
    'less_than',
    'greater_than_or_equal',
    'less_than_or_equal',
    'in',
    'not_in',
    'exists',
    'not_exists',
  ]),
  date: operators('date', [
    'equals',
    'not_equals',
    'greater_than',
    'less_than',
    'greater_than_or_equal',
    'less_than_or_equal',
    'exists',
    'not_exists',
  ]),
};

/** A Salesforce record Id or lookup (OwnerId, AccountId): SOQL compares it
 *  whole, never with LIKE. */
export function isSalesforceIdProperty(property?: HubSpotProperty): boolean {
  const raw = (property?.field_type ?? '').toLowerCase();
  return raw === 'id' || raw === 'reference';
}

const ID_OPERATORS = new Set(['equals', 'in', 'exists', 'not_exists']);

/** The operators a condition on `property` offers by default. */
export function triggerOperatorsFor(
  property: HubSpotProperty | undefined,
  fieldType: SalesforceFieldType
): TriggerOperator[] {
  // A saved fieldType outside the map (Clu wrote it) offers nothing.
  const offered = SALESFORCE_OPERATORS_BY_TYPE[fieldType] ?? [];
  return isSalesforceIdProperty(property)
    ? offered.filter((operator) => ID_OPERATORS.has(operator.value))
    : offered;
}

const NUMBER_TYPES = new Set([
  'number',
  'numeric',
  'currency',
  'int',
  'integer',
  'long',
  'double',
  'percent',
]);

const BOOLEAN_TYPES = new Set(['bool', 'boolean', 'booleancheckbox']);
const SALESFORCE_FIELD_TYPES = new Set<SalesforceTriggerFieldType>([
  'text',
  'picklist',
  'multipicklist',
  'boolean',
  'number',
  'date',
]);

const SALESFORCE_RELATIVE_DATE_RE =
  /^(TODAY|YESTERDAY|TOMORROW|THIS_WEEK|LAST_WEEK|NEXT_WEEK|THIS_MONTH|LAST_MONTH|NEXT_MONTH|THIS_QUARTER|LAST_QUARTER|NEXT_QUARTER|THIS_YEAR|LAST_YEAR|NEXT_YEAR|LAST_90_DAYS|NEXT_90_DAYS|(?:LAST|NEXT)_N_(?:DAYS|WEEKS|MONTHS):[1-9]\d*)$/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME_RE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?(?:Z|[+-]\d{2}:\d{2})$/;

/** Normalize Salesforce describe types to the backend's strict field families. */
export function resolveSalesforceFieldType(
  property?: HubSpotProperty
): SalesforceFieldType {
  const raw = (property?.field_type || property?.type || '').toLowerCase();
  if (raw === 'multipicklist') return 'multipicklist';
  if (
    raw === 'picklist' ||
    raw === 'combobox' ||
    raw === 'enumeration' ||
    raw === 'select'
  ) {
    return 'picklist';
  }
  if (raw === 'date' || raw === 'datetime') return 'date';
  if (NUMBER_TYPES.has(raw)) return 'number';
  if (BOOLEAN_TYPES.has(raw)) return 'boolean';
  return 'text';
}

export function isSalesforceDateTimeProperty(
  property?: HubSpotProperty
): boolean {
  return (
    (property?.field_type || property?.type || '').toLowerCase() === 'datetime'
  );
}

export function usableSalesforceOptions(
  property?: HubSpotProperty
): Array<{ value: string; label: string }> {
  return (property?.options ?? [])
    .map((option) => ({
      value: option.value ?? option.label ?? '',
      label: option.label ?? option.value ?? '',
    }))
    .filter((option) => option.value !== '');
}

export function defaultTriggerValue(
  fieldType: SalesforceFieldType,
  operator: string
): SalesforceTriggerCondition['value'] {
  if (VALUELESS_TRIGGER_OPERATORS.has(operator)) return undefined;
  if (fieldType === 'boolean') return true;
  if (
    fieldType === 'multipicklist' ||
    operator === 'in' ||
    operator === 'not_in'
  ) {
    return [];
  }
  return '';
}

export function isSalesforceRelativeDate(value: string): boolean {
  return SALESFORCE_RELATIVE_DATE_RE.test(value.toUpperCase());
}

export function isIsoDate(value: string): boolean {
  return ISO_DATE_RE.test(value);
}

export function isIsoDateTime(value: string): boolean {
  return ISO_DATETIME_RE.test(value);
}

export function sanitizeTriggerConditions(
  conditions: SalesforceTriggerGroup | null
): SalesforceTriggerGroup | null {
  if (!conditions) return null;
  return {
    operator: 'AND',
    conditions: conditions.conditions.map((condition) => {
      if (!VALUELESS_TRIGGER_OPERATORS.has(condition.operator)) {
        return condition;
      }
      const withoutValue = { ...condition };
      delete withoutValue.value;
      return withoutValue;
    }),
  };
}

function parseSavedCondition(
  value: unknown,
  index: number
): SalesforceTriggerCondition | null {
  if (!isRecord(value)) return null;
  if (typeof value.field !== 'string' || typeof value.operator !== 'string') {
    return null;
  }

  const condition: SalesforceTriggerCondition = {
    id:
      typeof value.id === 'string' && value.id.trim()
        ? value.id
        : `saved-condition-${index + 1}`,
    field: value.field,
    operator: value.operator,
  };

  if (
    typeof value.fieldType === 'string' &&
    SALESFORCE_FIELD_TYPES.has(value.fieldType as SalesforceTriggerFieldType)
  ) {
    condition.fieldType = value.fieldType as SalesforceTriggerFieldType;
  }

  if (
    typeof value.value === 'string' ||
    typeof value.value === 'boolean' ||
    (typeof value.value === 'number' && Number.isFinite(value.value))
  ) {
    condition.value = value.value;
  } else if (
    Array.isArray(value.value) &&
    value.value.every((item): item is string => typeof item === 'string')
  ) {
    condition.value = value.value;
  }

  return condition;
}

/**
 * Parse the untrusted JSONB trigger blob without assuming `conditions` is an
 * array. Valid draft conditions are salvaged, unknown keys are ignored, and a
 * malformed group becomes null so rendering can never crash on `.map`.
 */
export function parseSavedSalesforceTriggerConditions(
  value: unknown
): SalesforceTriggerGroup | null {
  if (!isRecord(value) || !Array.isArray(value.conditions)) return null;
  const conditions = value.conditions
    .map(parseSavedCondition)
    .filter(
      (condition): condition is SalesforceTriggerCondition => condition !== null
    );
  if (conditions.length === 0) return null;
  return { operator: 'AND', conditions };
}

export function parseSavedSalesforceTrigger(value: unknown): CrmTriggerConfig {
  if (
    !isRecord(value) ||
    typeof value.crmType !== 'string' ||
    value.crmType.toLowerCase() !== 'salesforce' ||
    typeof value.objectType !== 'string' ||
    value.objectType.toLowerCase() !== 'account'
  ) {
    return {
      crmType: 'salesforce',
      objectType: 'Account',
      conditions: null,
    };
  }
  return {
    crmType: 'salesforce',
    objectType: 'Account',
    conditions: parseSavedSalesforceTriggerConditions(value.conditions),
  };
}

/**
 * Validate the complete Salesforce Account trigger against the live describe
 * response. Returns the first user-facing problem, or null when the backend can
 * compile the payload without dropping or guessing any condition.
 */
export function validateSalesforceAccountTrigger(
  trigger: CrmTriggerConfig,
  properties: HubSpotProperty[]
): string | null {
  if (trigger.crmType.toLowerCase() !== 'salesforce') {
    return 'This alpha can only run from Salesforce.';
  }
  if (trigger.objectType !== 'Account') {
    return 'This alpha can only trigger from Salesforce accounts.';
  }

  const group = trigger.conditions;
  if (!group || group.conditions.length === 0) {
    return 'Add at least one Salesforce account condition.';
  }
  if (group.operator !== 'AND') {
    return 'The Salesforce alpha currently supports AND conditions only.';
  }
  if (properties.length === 0) {
    return 'Salesforce account properties are not available yet.';
  }

  const propertiesByName = new Map(
    properties.map((property) => [property.name, property])
  );

  for (let index = 0; index < group.conditions.length; index += 1) {
    const condition = group.conditions[index];
    const position = `Condition ${index + 1}`;
    if (!condition.field) return `${position} needs a Salesforce property.`;

    const property = propertiesByName.get(condition.field);
    if (!property) {
      return `${position} uses a property that is not in the live Salesforce schema.`;
    }
    if (property.filterable === false) {
      return `${position} uses a Salesforce property that cannot be filtered.`;
    }

    const fieldType = resolveSalesforceFieldType(property);
    if (condition.fieldType !== fieldType) {
      return `${position} has stale field metadata. Select the property again.`;
    }

    const supportedOperators = SALESFORCE_OPERATORS_BY_TYPE[fieldType];
    if (
      !supportedOperators.some(
        (operator) => operator.value === condition.operator
      )
    ) {
      return `${position} uses an operator Salesforce cannot apply to this property type.`;
    }

    if (VALUELESS_TRIGGER_OPERATORS.has(condition.operator)) {
      continue;
    }

    if (fieldType === 'boolean') {
      if (typeof condition.value !== 'boolean') {
        return `${position} needs a true or false value.`;
      }
      continue;
    }

    if (fieldType === 'number') {
      const raw =
        typeof condition.value === 'string' ||
        typeof condition.value === 'number'
          ? String(condition.value).trim()
          : '';
      if (raw === '' || !Number.isFinite(Number(raw))) {
        return `${position} needs a valid number.`;
      }
      continue;
    }

    if (fieldType === 'date') {
      const raw =
        typeof condition.value === 'string'
          ? condition.value.trim().toUpperCase()
          : '';
      const isRelative = isSalesforceRelativeDate(raw);
      const isExactDate = isSalesforceDateTimeProperty(property)
        ? isIsoDateTime(raw)
        : isIsoDate(raw);
      if (!isExactDate && !isRelative) {
        return `${position} needs a valid date or Salesforce date range.`;
      }
      if (isRelative && condition.operator !== 'equals') {
        return `${position} can use a relative date only with “is on”.`;
      }
      continue;
    }

    const options = usableSalesforceOptions(property);
    if (fieldType === 'picklist' || fieldType === 'multipicklist') {
      if (options.length === 0) {
        return `${position} has no selectable Salesforce values.`;
      }
      const allowed = new Set(options.map((option) => option.value));
      const needsArray =
        fieldType === 'multipicklist' ||
        condition.operator === 'in' ||
        condition.operator === 'not_in';

      if (needsArray) {
        if (
          !Array.isArray(condition.value) ||
          condition.value.length === 0 ||
          condition.value.some((value) => !allowed.has(value))
        ) {
          return `${position} needs at least one valid Salesforce value.`;
        }
      } else if (
        typeof condition.value !== 'string' ||
        !allowed.has(condition.value)
      ) {
        return `${position} needs a valid Salesforce value.`;
      }
      continue;
    }

    if (typeof condition.value !== 'string' || condition.value.trim() === '') {
      return `${position} needs a value.`;
    }
  }

  return null;
}
